/**
 * Sair de uma tela com alteração não salva pergunta antes (2026-10-26, a
 * pedido do Alexsander): "Salvar e sair", "Descartar alterações" ou
 * "Continuar editando".
 *
 * **Um provedor, vários formulários.** Uma tela pode ter mais de um
 * formulário com o próprio "Salvar" ao mesmo tempo (cada linha de prompt
 * padrão, as duas abas do Copiloto), e quem troca de aba costuma ser o
 * PAI de quem tem a alteração. Por isso cada formulário se registra com
 * `useUnsavedChanges(dirty, save, label)` e o provedor pergunta por todos;
 * o "Salvar e sair" grava cada um que tem alteração, em ordem, e só sai se
 * todos gravarem — uma falha deixa a pessoa na tela, com o erro que a
 * própria tela já mostra. O `label` é o que o aviso diz que ficou por
 * salvar ("Dados da empresa"), em vez de um vago "esta tela".
 *
 * **Duas portas de saída.**
 * - A ROTA (menu, outra empresa, voltar do navegador): o `useBlocker` do
 *   React Router. Só troca de CAMINHO conta — a busca (`?`) e o `#` são
 *   estado da própria tela (os atalhos de seção de Dados da empresa são
 *   `#`, e o filtro do dashboard mora na busca).
 * - A navegação DENTRO da tela (trocar de aba de Ajustes, de aba do CENA,
 *   de empresa no CENA por empresa): quem troca chama `useConfirmLeave()`
 *   com o que faria, e a troca só acontece depois da resposta.
 * Fechar ou recarregar a aba do navegador cai no aviso NATIVO
 * (`beforeunload`): o navegador não deixa trocar o texto nem os botões.
 *
 * **Sem o provedor, os hooks não fazem nada.** Os testes de render montam
 * as telas num `MemoryRouter`, que não é roteador de dados — e lá o
 * `useBlocker` quebra. O provedor mora no `RootLayout`, que só existe no
 * roteador de verdade.
 */

import { LoaderCircle, TriangleAlert, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { useBlocker } from "react-router-dom";

import { useDismiss } from "@/shared/hooks/useDismiss";

/** Grava o formulário; `true` se gravou. Falha deve aparecer na própria tela. */
export type SaveChanges = () => Promise<boolean>;

type Entry = { dirty: boolean; save?: SaveChanges; label?: string };
type EntryRef = { current: Entry };

type Registry = {
  register: (id: string, entry: EntryRef) => () => void;
  confirmLeave: (go: () => void) => void;
};

const UnsavedChangesContext = createContext<Registry | null>(null);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  // Num ref, e não em estado: o bloqueio e o `beforeunload` perguntam na
  // HORA da saída, e registrar não precisa redesenhar a casca inteira.
  const entries = useRef(new Map<string, EntryRef>());
  const dirtyEntries = useCallback(
    () => [...entries.current.values()].map(entry => entry.current).filter(entry => entry.dirty),
    []
  );

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname !== nextLocation.pathname && dirtyEntries().length > 0
  );
  // A saída de dentro da tela que espera a resposta.
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirtyEntries().length === 0) return;
      event.preventDefault();
      // Navegadores mais antigos só avisam com `returnValue` preenchido.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirtyEntries]);

  const register = useCallback((id: string, entry: EntryRef) => {
    entries.current.set(id, entry);
    return () => {
      entries.current.delete(id);
    };
  }, []);

  const confirmLeave = useCallback(
    (go: () => void) => {
      if (dirtyEntries().length === 0) go();
      else setPending(() => go);
    },
    [dirtyEntries]
  );

  const blocked = blocker.state === "blocked";
  const open = blocked || pending !== null;

  const leave = () => {
    if (blocked) {
      blocker.proceed();
      return;
    }
    const go = pending;
    setPending(null);
    go?.();
  };
  const stay = () => {
    if (blocked) blocker.reset();
    setPending(null);
  };
  const saveAndLeave = async () => {
    setSaving(true);
    try {
      for (const entry of dirtyEntries()) {
        if (!entry.save || !(await entry.save())) {
          stay();
          return;
        }
      }
      leave();
    } finally {
      setSaving(false);
    }
  };

  const dirty = open ? dirtyEntries() : [];
  // Formulário sem caminho de gravação (rascunho que não salva) não deixa
  // oferecer "Salvar e sair": sairia sem salvar o que dizia salvar.
  const canSave = dirty.every(entry => entry.save);
  const labels = [...new Set(dirty.map(entry => entry.label).filter((label): label is string => Boolean(label)))];

  return (
    <UnsavedChangesContext.Provider value={{ register, confirmLeave }}>
      {children}
      {open && (
        <UnsavedChangesDialog
          labels={labels}
          canSave={canSave}
          saving={saving}
          onSave={() => void saveAndLeave()}
          onDiscard={leave}
          onStay={stay}
        />
      )}
    </UnsavedChangesContext.Provider>
  );
}

/** Registra um formulário: enquanto `dirty`, sair da tela pergunta antes.
 *  `label` nomeia o que ficou por salvar no aviso ("Dados da empresa"). */
export function useUnsavedChanges(dirty: boolean, save?: SaveChanges, label?: string): void {
  const registry = useContext(UnsavedChangesContext);
  const id = useId();
  // Atualizado a cada render: a pergunta lê o `dirty` e o `save` de agora,
  // não os do momento do registro.
  const entry = useRef<Entry>({ dirty, save, label });
  entry.current = { dirty, save, label };
  useEffect(() => registry?.register(id, entry), [registry, id]);
}

const runNow = (go: () => void) => go();

/** Para trocas DENTRO da tela (aba, empresa escolhida): `confirmLeave(() => …)`. */
export function useConfirmLeave(): (go: () => void) => void {
  return useContext(UnsavedChangesContext)?.confirmLeave ?? runNow;
}

/** "Dados da empresa", "A e B", "A, B e C". */
function joinLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} e ${labels[labels.length - 1]}`;
}

function UnsavedChangesDialog({
  labels,
  canSave,
  saving,
  onSave,
  onDiscard,
  onStay
}: {
  labels: string[];
  canSave: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onStay: () => void;
}) {
  // Esc e clique fora são "continuar editando" — nunca descartar: o gesto
  // de fuga não pode custar o trabalho de ninguém.
  useDismiss(!saving, onStay);

  return createPortal(
    <>
      <div className="confirm-dialog-scrim" onClick={saving ? undefined : onStay} />
      <div className="confirm-dialog-anchor">
        <div
          className="unsaved-dialog"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="unsaved-dialog-title"
          aria-describedby="unsaved-dialog-description"
        >
          <button
            type="button"
            className="unsaved-dialog-close"
            onClick={onStay}
            disabled={saving}
            aria-label="Fechar e continuar editando"
          >
            <X size={16} />
          </button>
          <div className="unsaved-dialog-body">
            <span className="unsaved-dialog-icon" aria-hidden="true">
              <TriangleAlert size={20} />
            </span>
            <div className="unsaved-dialog-copy">
              <h3 id="unsaved-dialog-title">Há alterações não salvas</h3>
              <p id="unsaved-dialog-description">
                {labels.length > 0 ? (
                  <>
                    Você mudou <strong>{joinLabels(labels)}</strong> e ainda não salvou.
                  </>
                ) : (
                  "Você fez mudanças nesta tela e ainda não salvou."
                )}{" "}
                {canSave
                  ? "Salve antes de sair para não perder o que foi feito."
                  : "Se sair agora, essas mudanças se perdem."}
              </p>
            </div>
          </div>
          <div className="unsaved-dialog-actions">
            <button type="button" className="unsaved-dialog-discard" onClick={onDiscard} disabled={saving}>
              Descartar alterações
            </button>
            <div className="unsaved-dialog-actions-main">
              <button
                type="button"
                className="secondary-button"
                onClick={onStay}
                disabled={saving}
                autoFocus={!canSave}
              >
                Continuar editando
              </button>
              {canSave && (
                <button type="button" className="primary-button" onClick={onSave} disabled={saving} autoFocus>
                  {saving && <LoaderCircle className="spin" size={16} />}
                  {saving ? "Salvando…" : "Salvar e sair"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
